/** Memso brand colour assignments for the knowledge graph anchor.
 *
 *  Pulled from the same Tailwind palette the Header / Capture / Explore
 *  pivots already use, so adding a new node-type chip on the canvas
 *  reads as part of the same product, not a separate one. The order
 *  here is the order the type-filter chips render in. */

export interface NodeStyle {
  /** Solid fill for the node body. */
  fill: string;
  /** Border in default state. */
  border: string;
  /** Foreground text on the fill. */
  text: string;
  /** Subtle accent (used for the dot + outline ring). */
  accent: string;
  /** Tailwind-class equivalents for the chip rendering. */
  chipActive: string;
  chipInactive: string;
}

export interface EdgeStyle {
  /** Stroke colour for the edge. */
  stroke: string;
  /** Optional dashed pattern (e.g. "4 2"). */
  strokeDasharray?: string;
}

const FALLBACK_NODE: NodeStyle = {
  fill: "#f1f5f9",
  border: "#cbd5e1",
  text: "#0f172a",
  accent: "#94a3b8",
  chipActive: "bg-slate-600 text-white border-slate-600",
  chipInactive:
    "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700",
};

export const NODE_STYLES: Record<string, NodeStyle> = {
  note: {
    fill: "#e0f2fe",
    border: "#7dd3fc",
    text: "#0c4a6e",
    accent: "#0284c7",
    chipActive: "bg-sky-600 text-white border-sky-600",
    chipInactive:
      "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-200 border-sky-200 dark:border-sky-800",
  },
  task: {
    fill: "#dcfce7",
    border: "#86efac",
    text: "#14532d",
    accent: "#16a34a",
    chipActive: "bg-emerald-600 text-white border-emerald-600",
    chipInactive:
      "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
  },
  person: {
    fill: "#ede9fe",
    border: "#c4b5fd",
    text: "#4c1d95",
    accent: "#7c3aed",
    chipActive: "bg-violet-600 text-white border-violet-600",
    chipInactive:
      "bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200 border-violet-200 dark:border-violet-800",
  },
  place: {
    fill: "#fef3c7",
    border: "#fcd34d",
    text: "#78350f",
    accent: "#d97706",
    chipActive: "bg-amber-600 text-white border-amber-600",
    chipInactive:
      "bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800",
  },
  project: {
    fill: "#e0e7ff",
    border: "#a5b4fc",
    text: "#312e81",
    accent: "#4f46e5",
    chipActive: "bg-indigo-600 text-white border-indigo-600",
    chipInactive:
      "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800",
  },
  idea: {
    fill: "#ffe4e6",
    border: "#fda4af",
    text: "#881337",
    accent: "#e11d48",
    chipActive: "bg-rose-600 text-white border-rose-600",
    chipInactive:
      "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-200 border-rose-200 dark:border-rose-800",
  },
  source: {
    fill: "#f1f5f9",
    border: "#94a3b8",
    text: "#0f172a",
    accent: "#475569",
    chipActive: "bg-slate-600 text-white border-slate-600",
    chipInactive:
      "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700",
  },
};

export function nodeStyleFor(type: string): NodeStyle {
  return NODE_STYLES[type] ?? FALLBACK_NODE;
}

const FALLBACK_EDGE: EdgeStyle = { stroke: "#94a3b8" };

export const EDGE_STYLES: Record<string, EdgeStyle> = {
  mentions: { stroke: "#7c3aed" },
  related_to: { stroke: "#0284c7" },
  blocks: { stroke: "#e11d48", strokeDasharray: "5 3" },
  contains: { stroke: "#16a34a" },
  authored_by: { stroke: "#d97706" },
  derived_from: { stroke: "#4f46e5", strokeDasharray: "3 3" },
  tagged_with: { stroke: "#64748b", strokeDasharray: "2 4" },
};

export function edgeStyleFor(type: string): EdgeStyle {
  return EDGE_STYLES[type] ?? FALLBACK_EDGE;
}

/** Display order for the type-filter chips. Types not in this list
 *  fall to the end in alphabetical order. */
export const NODE_TYPE_ORDER = [
  "note",
  "task",
  "person",
  "place",
  "project",
  "idea",
  "source",
];
